/**
 * Which modal Escape belongs to.
 *
 * Every open modal listens for the key on the document, so two of them both
 * closed on a single press: backing out of the walkthrough's "Skip Heidi?"
 * threw away the ship sheet underneath it, notes and all. The stack settles
 * it: the key is the **topmost** modal's, and everything else that would like
 * it (Heidi's own Escape-to-skip) stands down while anything is open.
 *
 * A module, not a constant inside `Modal`, so the component file exports only
 * components and fast refresh keeps working.
 */
const stack: symbol[] = [];
const listeners = new Set<() => void>();

function changed() {
  listeners.forEach((listener) => listener());
}

/** Registers an open modal; the return value takes it off again. */
export function openModal(id: symbol): () => void {
  stack.push(id);
  changed();
  return () => {
    const at = stack.indexOf(id);
    if (at >= 0) stack.splice(at, 1);
    changed();
  };
}

/**
 * Told whenever a modal opens or closes, for what has to wait for the page
 * to be clear (a boss cutscene never starts over an open sheet).
 */
export function subscribeModals(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Whether `id` is the one on top, and so the one Escape is for. */
export function topModal(id: symbol): boolean {
  return stack.length > 0 && stack[stack.length - 1] === id;
}

/** Whether any modal is open at all. */
export function modalsOpen(): boolean {
  return stack.length > 0;
}
